import { useEffect, useState } from "react";
import { Spin, message } from "antd";
import YextApi, { YextReports, YextReportPoint, yextErrorMessage } from "../../api/YextApi";
import { PublisherLogo } from "./PublisherLogo";
import "./ListingsReportsTab.scss";

interface Props {
  locationId?: number;
}

const RANGES = [
  { days: 7, label: "7 days" },
  { days: 30, label: "30 days" },
  { days: 90, label: "90 days" },
];

function formatNum(n: number): string {
  return Math.round(n || 0).toLocaleString();
}

export function ListingsReportsTab({ locationId }: Props) {
  const [reports, setReports] = useState<YextReports | null>(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);

  useEffect(() => {
    if (!locationId) return;
    let cancelled = false;
    setLoading(true);
    YextApi.getReports(locationId, days)
      .then(({ data }) => {
        if (!cancelled) setReports(data);
      })
      .catch((e) => {
        if (!cancelled) message.error(yextErrorMessage(e, "Failed to load analytics."));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [locationId, days]);

  const kpis = reports
    ? [
        { label: "Impressions", value: reports.totals.impressions },
        { label: "Interactions", value: reports.totals.interactions },
        { label: "Profile Views", value: reports.totals.profileViews },
        { label: "Searches", value: reports.totals.searches },
      ]
    : [];

  return (
    <div className="listings-reports">
      <div className="rpt-bar">
        <h2 className="rpt-title">Listings Performance</h2>
        <div className="rpt-range">
          {RANGES.map((r) => (
            <button
              key={r.days}
              type="button"
              className={`rpt-range-btn${days === r.days ? " active" : ""}`}
              onClick={() => setDays(r.days)}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {loading && !reports ? (
        <div className="rpt-empty">
          <Spin />
        </div>
      ) : !reports || !reports.connected ? (
        <div className="rpt-empty">Publish your listing to start collecting analytics. Stats appear here once it’s live.</div>
      ) : (
        <div className={`rpt-body${loading ? " loading" : ""}`}>
          <div className="rpt-kpis">
            {kpis.map((k) => (
              <div className="rpt-kpi" key={k.label}>
                <div className="rpt-kpi-value">{formatNum(k.value)}</div>
                <div className="rpt-kpi-label">{k.label}</div>
              </div>
            ))}
          </div>

          <div className="rpt-card rpt-chart-card">
            <div className="rpt-card-head">
              <span className="rpt-card-title">Impressions &amp; interactions</span>
              <span className="rpt-legend">
                <span className="rpt-legend-item">
                  <span className="rpt-swatch impressions" /> Impressions
                </span>
                <span className="rpt-legend-item">
                  <span className="rpt-swatch interactions" /> Interactions
                </span>
              </span>
            </div>
            <TrendChart series={reports.series} />
          </div>

          <div className="rpt-section-title">By directory</div>
          <div className="rpt-publishers">
            {reports.publishers.map((p) => (
              <div className="rpt-card rpt-pub" key={p.publisher}>
                <div className="rpt-pub-head">
                  <PublisherLogo publisher={p.publisher} />
                  <span className="rpt-pub-name">{p.publisher}</span>
                </div>
                <div className="rpt-pub-stat">
                  <span className="rpt-pub-value">{formatNum(p.primaryValue)}</span>
                  <span className="rpt-pub-label">{p.primaryLabel}</span>
                </div>
                {p.secondaryLabel && (
                  <div className="rpt-pub-stat secondary">
                    <span className="rpt-pub-value">{formatNum(p.secondaryValue || 0)}</span>
                    <span className="rpt-pub-label">{p.secondaryLabel}</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="rpt-section-title">Listing health</div>
          <div className="rpt-health">
            <div className="rpt-card rpt-health-card">
              <div className="rpt-kpi-value">{formatNum(reports.health.listingsLive)}</div>
              <div className="rpt-kpi-label">Live listings</div>
            </div>
            <div className="rpt-card rpt-health-card">
              <div className="rpt-kpi-value">{Math.round(reports.health.accuracy)}%</div>
              <div className="rpt-kpi-label">Listing accuracy</div>
            </div>
            <div className="rpt-card rpt-health-card">
              <div className="rpt-kpi-value">{formatNum(reports.health.duplicatesSuppressed)}</div>
              <div className="rpt-kpi-label">Duplicates suppressed</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Dependency-free SVG line chart of impressions + interactions over the series.
function TrendChart({ series }: { series: YextReportPoint[] }) {
  if (series.length === 0) {
    return <div className="rpt-chart-empty">No activity in this period yet.</div>;
  }

  const W = 640;
  const H = 220;
  const padX = 10;
  const padTop = 14;
  const padBottom = 26;
  const n = series.length;
  const maxV = Math.max(1, ...series.map((p) => Math.max(p.impressions, p.interactions)));

  const x = (i: number) => padX + (n === 1 ? (W - 2 * padX) / 2 : (i * (W - 2 * padX)) / (n - 1));
  const y = (v: number) => H - padBottom - (v / maxV) * (H - padTop - padBottom);

  const linePath = (key: "impressions" | "interactions") =>
    series.map((p, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`).join(" ");

  const areaPath =
    `M${x(0).toFixed(1)},${(H - padBottom).toFixed(1)} ` +
    series.map((p, i) => `L${x(i).toFixed(1)},${y(p.impressions).toFixed(1)}`).join(" ") +
    ` L${x(n - 1).toFixed(1)},${(H - padBottom).toFixed(1)} Z`;

  const fmtDate = (d: string) => {
    const dt = new Date(d);
    return Number.isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <svg className="rpt-chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Impressions and interactions over time">
      <line className="rpt-axis" x1={padX} y1={H - padBottom} x2={W - padX} y2={H - padBottom} vectorEffect="non-scaling-stroke" />
      <path className="rpt-area" d={areaPath} />
      <path className="rpt-line impressions" d={linePath("impressions")} vectorEffect="non-scaling-stroke" />
      <path className="rpt-line interactions" d={linePath("interactions")} vectorEffect="non-scaling-stroke" />
      <text className="rpt-axis-label" x={padX} y={H - 8}>
        {fmtDate(series[0].date)}
      </text>
      <text className="rpt-axis-label end" x={W - padX} y={H - 8} textAnchor="end">
        {fmtDate(series[n - 1].date)}
      </text>
      <text className="rpt-axis-label" x={padX} y={padTop}>
        {formatNum(maxV)}
      </text>
    </svg>
  );
}
